import { useEffect, useRef, useState, useMemo } from 'react';
import { LayoutGrid, Map, Rows3, Image } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { withFileAuth } from '@/utils/api';
import { TableCard } from './TableCard';
import { TableLegend } from './TableLegend';
import { TableRowsGrid } from './TableRowsGrid';
import { fuseGroups } from './tableStates';

const ROWS_THRESHOLD = 30;

const ViewToggle = ({ view, setView, planDisabled }) => (
  <div className="flex items-center gap-1 bg-muted rounded-full p-1" data-testid="table-view-toggle">
    {[['cards', 'Tarjetas', LayoutGrid], ['plan', 'Plano', Map], ['rows', 'Filas', Rows3]].map(([k, label, Icon]) => (
      <Button key={k} size="sm" variant={view === k ? 'default' : 'ghost'} className="rounded-full h-8"
        disabled={k === 'plan' && planDisabled} data-testid={`table-view-${k}`} onClick={() => setView(k)}>
        <Icon className="w-4 h-4 mr-1" />{label}
      </Button>
    ))}
  </div>
);

const PlanView = ({ units, mapImage, editable, onPositionChange, cardProps }) => {
  const ref = useRef(null);
  const [dragId, setDragId] = useState(null);
  // Unidades sin posición: grilla automática para que no se apilen
  const placed = useMemo(() => {
    const free = units.filter(u => u.position_x == null || u.position_y == null);
    const cols = Math.max(1, Math.ceil(Math.sqrt(free.length * 1.6)));
    const rows = Math.max(1, Math.ceil(free.length / cols));
    return units.map(u => {
      const i = free.indexOf(u);
      if (i < 0) return u;
      return { ...u, position_x: 10 + (i % cols) * (80 / Math.max(cols - 1, 1)), position_y: 12 + Math.floor(i / cols) * (76 / Math.max(rows - 1, 1)) };
    });
  }, [units]);
  const drop = (e) => {
    e.preventDefault();
    if (!dragId || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    onPositionChange?.(dragId, ((e.clientX - r.left) / r.width) * 100, ((e.clientY - r.top) / r.height) * 100);
    setDragId(null);
  };
  return (
    <div ref={ref} onDragOver={(e) => e.preventDefault()} onDrop={drop} data-testid="table-plan"
      className={`relative w-full min-h-[560px] h-[70vh] rounded-2xl border overflow-hidden ${editable ? 'border-dashed border-primary' : 'border-border'}`}
      style={{ background: mapImage ? `center / contain no-repeat url(${withFileAuth(mapImage)})` : 'hsl(var(--muted))' }}>
      {!mapImage && (
        <div className="absolute inset-0 grid place-items-center text-muted-foreground pointer-events-none">
          <div className="text-center"><Image className="w-10 h-10 mx-auto mb-2" /><p className="text-sm">Sin plano de fondo</p></div>
        </div>
      )}
      {placed.map(u => (
        <div key={u.unit_id} draggable={editable} onDragStart={() => setDragId(u.unit_id)}
          className={`absolute -translate-x-1/2 -translate-y-1/2 ${editable ? 'cursor-move' : ''}`}
          style={{ left: `${u.position_x ?? 50}%`, top: `${u.position_y ?? 50}%` }}>
          <TableCard unit={u} compact {...cardProps(u)} />
        </div>
      ))}
    </div>
  );
};

export const TableMap = ({ units, mapImage, showAmount, selectedId, onSelect, onDoubleClick, onClean,
  editable = false, onPositionChange, defaultView, now, hideToggle = false, planOnly = false }) => {
  const fused = useMemo(() => fuseGroups(units), [units]);
  const autoView = () => defaultView || (planOnly ? 'plan' : units.length > ROWS_THRESHOLD ? 'rows' : 'cards');
  const [view, setViewState] = useState(autoView);
  const chosen = useRef(false);
  const setView = (v) => { chosen.current = true; setViewState(v); };
  useEffect(() => { if (!chosen.current) setViewState(autoView()); }, [units.length > ROWS_THRESHOLD]); // eslint-disable-line react-hooks/exhaustive-deps
  const counts = useMemo(() => fused.reduce((a, u) => ({ ...a, [u.state]: (a[u.state] || 0) + 1 }), {}), [fused]);
  const cardProps = (u) => ({
    now, showAmount, selected: selectedId === u.unit_id, onClean,
    onClick: () => onSelect?.(u), onDoubleClick: () => onDoubleClick?.(u),
  });
  const current = editable ? 'plan' : view;
  return (
    <div className="space-y-4" data-testid="table-map">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <TableLegend counts={counts} />
        {!hideToggle && !editable && <ViewToggle view={view} setView={setView} planDisabled={false} />}
      </div>
      {current === 'plan' && <PlanView units={fused} mapImage={mapImage} editable={editable} onPositionChange={onPositionChange} cardProps={cardProps} />}
      {current === 'rows' && <TableRowsGrid units={fused} onSelect={onSelect} onDoubleClick={onDoubleClick} selectedId={selectedId} />}
      {current === 'cards' && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" data-testid="table-cards">
          {fused.map(u => <TableCard key={u.unit_id} unit={u} {...cardProps(u)} />)}
        </div>
      )}
    </div>
  );
};
